// UserContext.js

import React, { createContext, useState, useEffect } from "react";
import axios from "axios";

export const UserContext = createContext();

export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // 🔐 Fetch logged-in user on app load
  useEffect(() => {
    const fetchUser = async () => {
      const token = localStorage.getItem("token");

      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/users/me`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );

        setUser(response.data);
      } catch (error) {
        console.error("Session error:", error);

        // Token expired or invalid → logout silently
        if (error.response?.status === 401) {
          localStorage.removeItem("token");
          setUser(null);
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  // 🔑 Login
  const login = async (credentials) => {
    try {
      setAuthError(null);
      setLoading(true);

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/users/login`,
        credentials,
      );

      const token = response.data.token;
      localStorage.setItem("token", token);

      const userResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/users/me`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      setUser(userResponse.data);
      return true;
    } catch (error) {
      console.error("Login error:", error);

      if (error.response) {
        setAuthError(
          error.response.data?.message || "Invalid email or password",
        );
      } else if (error.request) {
        setAuthError("Unable to connect to server. Please try again.");
      } else {
        setAuthError("Something went wrong. Please try again.");
      }

      return false;
    } finally {
      setLoading(false);
    }
  };

  // 🚪 Logout
  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
    setAuthError(null);
    window.location.assign("/login");
  };

  return (
    <UserContext.Provider
      value={{
        user,
        loading,
        authError,
        login,
        logout,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};
